import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useDriverLoginSession } from '@/hooks/useDriverLoginSession';
import { monitoring } from '@/lib/monitoring';
import { Truck, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import DriverAuth from './DriverAuth';

const DriverApp = lazy(() => import('./DriverApp'));

const DRIVER_MODE_KEY = 'gonx-driver-mode';
const LOADING_TIMEOUT = 15000;

const DriverEntry = () => {
  const {
    user,
    company,
    loading: authLoading,
    authError,
    refreshProfile,
    refreshOrgClaims,
  } = useAuth();
  const {
    isValid,
    isLoading: sessionLoading,
    error: sessionError,
    createSession,
    sessionConflict,
    forceLogoutOtherDevice,
    cancelConflict,
  } = useDriverLoginSession();
  const [showRetry, setShowRetry] = useState(false);
  const driverProfileWarnRef = useRef<string | null>(null);
  const [profileRetryBusy, setProfileRetryBusy] = useState(false);

  const isDriverSessionConfirmed = !!user && isValid && !!company;
  const profileLoadStuck =
    !!user?.id &&
    isValid &&
    !company &&
    !authLoading &&
    !sessionLoading;

  useEffect(() => {
    localStorage.setItem(DRIVER_MODE_KEY, 'true');
  }, []);

  useEffect(() => {
    if (authLoading || sessionLoading) {
      const timeout = setTimeout(() => {
        setShowRetry(true);
      }, LOADING_TIMEOUT);
      return () => clearTimeout(timeout);
    }
    setShowRetry(false);
  }, [authLoading, sessionLoading]);

  useEffect(() => {
    const stuck =
      !!user?.id &&
      isValid &&
      !company &&
      !authLoading &&
      !sessionLoading;
    if (!stuck) {
      driverProfileWarnRef.current = null;
      return;
    }
    const uid = user.id;
    if (driverProfileWarnRef.current === uid) return;
    monitoring.captureWarning('driver_company_null_on_ready', {
      userId: uid,
      isValid,
      hasAuthError: !!authError,
    });
    driverProfileWarnRef.current = uid;
  }, [user, user?.id, isValid, company, authLoading, sessionLoading, authError]);

  const handleHardRetry = async () => {
    localStorage.removeItem('driver_session_token');
    setShowRetry(false);

    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        await Promise.all(keys.map((key) => caches.delete(key)));
      } catch {
        /* ignorar */
      }
    }

    sessionStorage.clear();
    window.location.reload();
  };

  const handleProfileRetry = async () => {
    setProfileRetryBusy(true);
    try {
      await refreshOrgClaims();
      await refreshProfile();
    } finally {
      setProfileRetryBusy(false);
    }
  };

  if (profileLoadStuck) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center p-4">
        <Alert className="max-w-md border-orange-500/40 bg-slate-950/90 text-blue-100">
          <AlertDescription className="space-y-4 text-sm leading-relaxed">
            <p>
              {authError?.trim()
                ? authError
                : 'Não foi possível carregar seu perfil. Verifique sua conexão e tente novamente.'}
            </p>
            <Button
              variant="outline"
              className="w-full bg-orange-500/20 border-orange-500/50 text-orange-200 hover:bg-orange-500/30"
              disabled={profileRetryBusy}
              onClick={handleProfileRetry}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${profileRetryBusy ? 'animate-spin' : ''}`} />
              Tentar novamente
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // DRIVER-LOGIN-ERRO (I.160): sem usuário, a DriverAuth fica na tela (cuida do próprio carregamento e guarda a
  // mensagem de erro e o e-mail); "Carregando..." só quando já há usuário
  if (!!user && !isDriverSessionConfirmed && (authLoading || sessionLoading)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="bg-orange-500/20 p-4 rounded-xl">
            <Truck className="h-12 w-12 text-orange-500 animate-pulse" />
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
          </div>
          <p className="text-blue-200/70 text-sm">
            {sessionError || 'Carregando...'}
          </p>

          {showRetry && (
            <Button
              variant="outline"
              onClick={handleHardRetry}
              className="mt-4 bg-orange-500/20 border-orange-500/50 text-orange-300 hover:bg-orange-500/30"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar novamente
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (!user || !isValid) {
    return (
      <DriverAuth
        createSession={createSession}
        sessionConflict={sessionConflict}
        forceLogoutOtherDevice={forceLogoutOtherDevice}
        cancelConflict={cancelConflict}
      />
    );
  }

  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="bg-orange-500/20 p-4 rounded-xl">
            <Truck className="h-12 w-12 text-orange-500 animate-pulse" />
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    }>
      <DriverApp />
    </Suspense>
  );
};

export default DriverEntry;
